import { useState } from 'react';
import { useAdminSettings } from '@/hooks/useAdminSettings';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Plus } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

export const SystemSettings = () => {
  const { settings, loading, updateSetting } = useAdminSettings();
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [newDescription, setNewDescription] = useState('');

  const handleAddSetting = () => {
    try {
      const parsedValue = JSON.parse(newValue);
      updateSetting(newKey, parsedValue, newDescription);
      setNewKey('');
      setNewValue('');
      setNewDescription('');
    } catch {
      updateSetting(newKey, newValue, newDescription);
      setNewKey('');
      setNewValue('');
      setNewDescription('');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>System Settings</CardTitle>
            <Dialog>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Setting
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Setting</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Setting Key</Label>
                    <Input
                      placeholder="e.g., max_free_images"
                      value={newKey}
                      onChange={(e) => setNewKey(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Value (JSON or text)</Label>
                    <Textarea
                      placeholder='e.g., 10 or {"limit": 10}'
                      value={newValue}
                      onChange={(e) => setNewValue(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input
                      placeholder="What this setting controls"
                      value={newDescription}
                      onChange={(e) => setNewDescription(e.target.value)}
                    />
                  </div>
                  <Button onClick={handleAddSetting} className="w-full">
                    Save Setting
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {settings.map((setting) => (
              <Card key={setting.id} className="border-muted">
                <CardContent className="pt-6">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-base font-semibold">{setting.setting_key}</Label>
                      <span className="text-xs text-muted-foreground">
                        Updated: {new Date(setting.updated_at).toLocaleString()}
                      </span>
                    </div>
                    {setting.description && (
                      <p className="text-sm text-muted-foreground">{setting.description}</p>
                    )}
                    <div className="bg-muted p-3 rounded-md">
                      <code className="text-sm">
                        {JSON.stringify(setting.setting_value, null, 2)}
                      </code>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {settings.length === 0 && (
              <p className="text-center text-muted-foreground py-8">
                No settings configured yet. Add your first setting!
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
